/**
 * Report sections of Kuviteltu tulevaisuus (future_reimagined.v1, #946):
 * how the solutions affect each other, and the imagined future of the locked
 * set: its course in each scenario (#1062: fates, the effect verdict, the first
 * moments) and which solutions hold in every scenario. Each renders only when the
 * chain includes it and it has content. The data endpoint needs a sign-in, like
 * the systemic v2 and foresight sections.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Section, SectionTitle, SectionSubtitle, Paragraph, Divider, SITRA } from './reportStyles';
import SectionGate from './SectionGate';
import { robustness } from '../../screens/future-reimagined/robustness';
import { worldTabs, tabLabel } from '../../screens/future-reimagined/worldTabs';

export const FUTURE_NAV_KEYS = { 'future-interplay': 'futureInterplay', 'future-timeline': 'futureTimeline' };
export const FUTURE_SECTIONS = Object.keys(FUTURE_NAV_KEYS);

const RELATION_ACCENT = { conflict: '#c0392b', overlap: '#d4a017', competing: '#e07b2a', enables: '#2f6fb3', reinforces: '#2e9e5b' };
const FATE_ACCENT = { realised: '#2e9e5b', delayed: '#2f6fb3', diminished: '#d4a017', failed: '#c0392b' };

const List = styled.ul`margin: 12px 0 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px;`;
const Item = styled.li`
  padding: 12px 14px; border-radius: 4px; border: 1px solid ${SITRA.border};
  border-left: 4px solid ${(p) => p.$accent || SITRA.border}; background: ${SITRA.white}; color: ${SITRA.textBody}; line-height: 1.55;
  .title { font-weight: 600; }
  .meta { margin-top: 4px; font-size: 14px; color: ${SITRA.textMuted}; }
  p { margin: 6px 0 0; }
`;
const Grid = styled.table`
  width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 14px;
  th, td { text-align: left; padding: 8px; border-bottom: 1px solid ${SITRA.border}; vertical-align: top; }
  th { color: ${SITRA.textMuted}; font-weight: 600; }
`;

export function useFutureReportData(instanceId, enabled) {
  const [state, setState] = useState(null);
  const want = FUTURE_SECTIONS.some((id) => enabled(id));
  useEffect(() => {
    if (!instanceId || !want) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.FUTURE_STATE(instanceId), { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((body) => { if (!cancelled) setState(body); });
    return () => { cancelled = true; };
  }, [instanceId, want]);
  const available = new Set();
  if (state?.interplay?.edges?.some((e) => e.relation !== 'independent')) available.add('future-interplay');
  if (state?.timelines?.some((tl) => tl.frameKind === 'world' && tl.status === 'done')) available.add('future-timeline');
  return { data: state, available };
}

function Interplay({ state }) {
  const { t } = useTranslation();
  const title = new Map(state.solutions.map((s) => [s.id, s.title]));
  const edges = state.interplay.edges;
  const shown = edges.filter((e) => e.relation !== 'independent');
  const independent = edges.length - shown.length;
  return (
    <>
      <Paragraph>{t('futureReimagined.report.interplayIntro')}</Paragraph>
      <List>
        {shown.map((e) => {
          const [a, b] = e.relation === 'enables' && e.from === e.b ? [e.b, e.a] : [e.a, e.b];
          return (
            <Item key={`${e.a}:${e.b}`} $accent={RELATION_ACCENT[e.relation]}>
              <div className="title">{t(`futureReimagined.relation.${e.relation}`)}: {title.get(a)} {e.relation === 'enables' ? '→' : '↔'} {title.get(b)}</div>
              <p>{e.explanation}</p>
              {e.uncertainty && <div className="meta">{t('futureReimagined.relationCard.uncertainty', { text: e.uncertainty })}</div>}
            </Item>
          );
        })}
      </List>
      {independent > 0 && <Paragraph>{t('futureReimagined.report.independent', { count: independent })}</Paragraph>}
    </>
  );
}

/** Moments per scenario in the report: the narrative's first few, enough to show the course's character. */
const REPORT_MOMENTS = 4;

function Timeline({ state }) {
  const { t } = useTranslation();
  const title = (id) => state.solutions.find((s) => s.id === id)?.title || t('futureReimagined.locked.deleted');
  const tabs = worldTabs(state.timelines).filter((tab) => tab.timeline.status === 'done');
  const runs = tabs.map((tab) => tab.timeline);
  return (
    <>
      <Paragraph>{t('futureReimagined.report.timelineIntro')}</Paragraph>
      <Paragraph>{t('futureReimagined.report.locked', { names: state.run.solutionIds.map(title).join('; ') })}</Paragraph>
      {state.run.overrideReason && <Paragraph>{t('futureReimagined.report.override', { reason: state.run.overrideReason })}</Paragraph>}

      {tabs.map((tab) => {
        const tl = tab.timeline;
        const verdict = tl.effect?.set?.verdict;
        return (
          <React.Fragment key={tab.id}>
            <SectionSubtitle>{t('futureReimagined.scenario.label')}: {tabLabel(tab, 80)}</SectionSubtitle>
            {verdict && <Paragraph>{t('futureReimagined.effect.wall', { verdict: t(`futureReimagined.effect.verdict.${verdict}`) })}{tl.effect.set.why ? `: ${tl.effect.set.why}` : ''}</Paragraph>}
            <List>
              {tl.fates.map((f) => (
                <Item key={f.solutionId} $accent={FATE_ACCENT[f.fate]}>
                  <div className="title">{title(f.solutionId)}: {t(`futureReimagined.fate.${f.fate}`)} {f.year ?? ''}</div>
                  <p>{f.why}</p>
                  {f.whatWouldChange && <div className="meta">{t('futureReimagined.course.wouldChange', { text: f.whatWouldChange })}</div>}
                </Item>
              ))}
            </List>
            {tl.narrative?.length > 0 && (
              <List>
                {tl.narrative.slice(0, REPORT_MOMENTS).map((p, i) => (
                  <Item key={i}>
                    <div className="title">{p.year} · {p.title}</div>
                    <p>{p.narrative}</p>
                    <div className="meta">{t('futureReimagined.relationCard.uncertainty', { text: p.uncertainty })}</div>
                  </Item>
                ))}
              </List>
            )}
          </React.Fragment>
        );
      })}

      {runs.length > 1 && (
        <>
          <SectionSubtitle>{t('futureReimagined.robustness.title')}</SectionSubtitle>
          <Grid>
            <thead>
              <tr><th aria-hidden />{tabs.map((tab) => <th key={tab.id}>{tabLabel(tab)}</th>)}<th>{t('futureReimagined.robustness.verdict')}</th></tr>
            </thead>
            <tbody>
              {robustness(state.run.solutionIds, runs).map((r) => (
                <tr key={r.solutionId}>
                  <th scope="row">{title(r.solutionId)}</th>
                  {r.cells.map((c) => (
                    <td key={c.timelineId} style={{ color: c.fate ? FATE_ACCENT[c.fate.fate] : SITRA.textMuted }}>
                      {c.fate ? `${t(`futureReimagined.fate.${c.fate.fate}`)} ${c.fate.year ?? ''}` : '—'}
                    </td>
                  ))}
                  <td>{t(`futureReimagined.robustness.${r.verdict}`)}</td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </>
      )}
    </>
  );
}

const BLOCKS = { 'future-interplay': Interplay, 'future-timeline': Timeline };

export default function ReportFutureSections({ ids, data, available, show, accessOf }) {
  const { t } = useTranslation();
  if (!data) return null;
  return ids.filter((id) => available.has(id) && show(id)).map((id) => {
    const Block = BLOCKS[id];
    return (
      <SectionGate key={id} access={accessOf(id)} gateTitle={t(`report.nav.${FUTURE_NAV_KEYS[id]}`)}>
        <Section id={id}>
          <SectionTitle>{t(`report.nav.${FUTURE_NAV_KEYS[id]}`)}</SectionTitle>
          <Block state={data} />
        </Section>
        <Divider />
      </SectionGate>
    );
  });
}
